import { createContext } from 'react';
import type { BuildingType, BuildingZone, Planet, PlayerRace, ScienceId } from '@kaeliux/shared';
import type { PlanetActionOptions } from '../services/planet.service';

/**
 * Everything the shared planet screens (surface, building popover, zone
 * list, requirement tiles) need to know about WHICH planet they're showing:
 * where its data and mutations come from, which routes it links to, and
 * what the viewer may do on it. A personal planet reads the player's own
 * stores (`usePlanetView`); the alliance Team planet overrides the whole
 * thing via `TeamPlanetViewContext` (see `TeamPlanetLayout`).
 *
 * Kept free of service imports so light components can read the context
 * without initializing Firebase.
 */
export interface PlanetView {
    kind: 'personal' | 'teamPlanet';
    /** False while data the screens gate on (a personal planet's sciences) is still loading. */
    ready: boolean;
    canManage: boolean;
    /** Shown in place of build/upgrade/cancel controls when `canManage` is false. */
    readOnlyReason: string | null;
    allowRush: boolean;
    allowDestroy: boolean;
    scienceLevels: Partial<Record<ScienceId, number>>;
    buildingQueueLimit: number;
    /**
     * The Platinum "Queue max" building action (issue #219): `granted` offers
     * it, `locked` shows it as a Platinum upsell, `hidden` never shows it.
     */
    queueMaxAccess: 'granted' | 'locked' | 'hidden';
    /** True when a higher premium tier would raise `buildingQueueLimit` — drives "Unlock more slots". */
    canUnlockMoreSlots: boolean;
    homeRoute: string;
    zoneRoute: (zone: BuildingZone, highlight?: string) => string;
    screenRoute: (buildingType: BuildingType) => string | undefined;
    /** Race a building's sprite is drawn in — `undefined` means the viewer's own race. */
    buildingRace: (buildingType: BuildingType) => PlayerRace | undefined;
    upgradeBuilding: (planet: Planet, buildingType: BuildingType, options: PlanetActionOptions, plotId?: string) => Promise<void>;
    /** Present only where `queueMaxAccess` can be `granted`. Resolves to the number of levels actually queued. */
    queueMaxBuildingLevels?: (planet: Planet, buildingType: BuildingType, options: PlanetActionOptions, plotId?: string, maxLevels?: number) => Promise<number>;
    cancelBuildingUpgrade: (planet: Planet, eventId: number, options: PlanetActionOptions) => Promise<void>;
}

export const TeamPlanetViewContext = createContext<PlanetView | null>(null);

export function zoneRouteFrom(base: string) {
    return (zone: BuildingZone, highlight?: string) =>
        `${base}/zone/${zone}${highlight ? `?highlight=${highlight}` : ''}`;
}
